import { useEffect, useRef, useState } from 'react';
import QRCode from 'qrcode';
import { Loader2, QrCode } from 'lucide-react';
import { cn } from '@/lib/utils';

interface TotpQrCodeProps {
  value: string;
  className?: string;
}

export function TotpQrCode({ value, className }: TotpQrCodeProps) {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const [state, setState] = useState<'loading' | 'ready' | 'error'>('loading');

  useEffect(() => {
    let cancelled = false;
    setState('loading');

    if (!value || !canvasRef.current) {
      setState('error');
      return;
    }

    QRCode.toCanvas(canvasRef.current, value, {
      width: 192,
      margin: 1,
      // True-neutral ink on white — QR contrast must stay fixed regardless of
      // theme, but the ink matches the matte-black palette (not blue-tinted slate).
      color: {
        dark: '#0a0a0a',
        light: '#ffffff',
      },
    })
      .then(() => {
        if (!cancelled) setState('ready');
      })
      .catch(() => {
        if (!cancelled) setState('error');
      });

    return () => {
      cancelled = true;
    };
  }, [value]);

  return (
    <div
      className={cn(
        'relative flex h-56 w-56 items-center justify-center rounded-lg border bg-card p-4',
        className
      )}
    >
      {state === 'loading' && (
        <div className="absolute inset-4 flex flex-col items-center justify-center rounded-md bg-muted/40 text-sm text-muted-foreground">
          <Loader2 className="mb-2 h-5 w-5 animate-spin" />
          Generating QR code
        </div>
      )}
      {state === 'error' && (
        <div className="absolute inset-4 flex flex-col items-center justify-center rounded-md bg-warning/10 text-center text-sm text-warning">
          <QrCode className="mb-2 h-5 w-5" />
          Use the setup key below.
        </div>
      )}
      <canvas
        ref={canvasRef}
        className={cn('h-48 w-48', state !== 'ready' && 'opacity-0')}
        aria-label="TOTP QR code"
      />
    </div>
  );
}
